import * as React from 'react'
import { addDays, format, parseISO } from 'date-fns'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { trackIdsOf } from '@lib/helpers/event-track'
import { isPlausibleEventDay, MAX_EVENT_DAY } from '@lib/input/event.input'
import { calendarDay, type CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Field } from '@/components/ui/field'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useBookedDays } from '@/hooks/useBookedDays'
import { HELD_LABEL, useHeldDays } from '@/hooks/useHeldDays'
import { usePlaceHold, useReleaseHold } from '@/hooks/useEvents'
import { bookedLabel, todayIso } from '@/lib/bookedDays'
import { requestedDateLabel } from './RequestedDates'

/** How long a new hold lasts unless someone says otherwise. */
const DEFAULT_HOLD_DAYS = 7

/**
 * Until when a new hold of `day` lasts by default: a week from today, but never
 * past the day itself, since a hold ends on or before the day it holds.
 */
export function defaultHoldUntil(day: CalendarDay, today: CalendarDay): CalendarDay {
  const week = calendarDay(format(addDays(parseISO(today), DEFAULT_HOLD_DAYS), 'yyyy-MM-dd'))
  return week < day ? week : day
}

/** The day a new hold starts on: the earliest requested date not yet passed, if any. */
export function defaultHoldDay(requested: readonly CalendarDay[], today: CalendarDay): CalendarDay | '' {
  return requested.find((day) => day >= today) ?? ''
}

interface HoldControlProps {
  event: EventDetailDto
  onError: (message: string | null) => void
}

/**
 * Holding a date for an inquiry until a chosen day (docs/funnel-stages.spec.md).
 * The hold shows on the calendar and in other bookings' date pickers, and never
 * stops anyone: it is spoken for, not taken. Booking the inquiry, or marking it
 * lost, releases it.
 */
export function HoldControl({ event, onError }: HoldControlProps) {
  const place = usePlaceHold(event.id)
  const release = useReleaseHold(event.id)
  const [editing, setEditing] = React.useState(false)
  const [day, setDay] = React.useState<CalendarDay | ''>('')
  const [until, setUntil] = React.useState<CalendarDay | ''>('')
  const [month, setMonth] = React.useState<CalendarDay | null>(null)
  const today = todayIso()
  const trackIds = trackIdsOf(event)
  const shown = month ?? (day === '' ? today : day)
  const booked = useBookedDays({ venueId: event.venueId, trackIds, month: shown, excludeEventId: event.id })
  const held = useHeldDays({ venueId: event.venueId, trackIds, month: shown, excludeEventId: event.id })
  const busy = place.isPending || release.isPending
  const hold = event.hold

  function startEditing(): void {
    const start = hold?.date ?? defaultHoldDay(event.requestedDates, today)
    setDay(start)
    setUntil(hold?.endsOn ?? (start === '' ? '' : defaultHoldUntil(start, today)))
    setMonth(null)
    onError(null)
    setEditing(true)
  }

  function chooseDay(next: CalendarDay | ''): void {
    if (next !== '' && !isPlausibleEventDay(next)) return
    setDay(next)
    // A hold can't outlast its day, so a later day keeps the end and an earlier one pulls it in.
    if (next === '') setUntil('')
    else if (until === '' || until > next) setUntil(defaultHoldUntil(next, today))
  }

  function submit(): void {
    if (day === '' || until === '') return
    onError(null)
    place.mutate(
      { date: day, endsOn: until },
      { onSuccess: () => setEditing(false), onError: (e) => onError(requestErrorMessage(e)) },
    )
  }

  if (editing) {
    return (
      <div className="space-y-3 rounded-[var(--radius-control)] border border-line p-3">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Day to hold" htmlFor="hold-day">
            <DatePicker
              id="hold-day"
              value={day}
              onChange={chooseDay}
              min={today}
              max={MAX_EVENT_DAY}
              marked={booked}
              markedLabel={bookedLabel(trackIds.length)}
              held={held}
              heldLabel={HELD_LABEL}
              onMonthChange={setMonth}
              className="w-48"
            />
          </Field>
          <Field label="Hold until" htmlFor="hold-until">
            <DatePicker
              id="hold-until"
              value={until}
              onChange={(next) => setUntil(next)}
              disabled={day === ''}
              min={today}
              {...(day === '' ? {} : { max: day })}
              className="w-48"
            />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button pending={place.isPending} type="button" size="sm" disabled={day === '' || until === '' || busy} onClick={submit}>
            Hold
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  if (hold === null) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={startEditing}>
        Hold a date
      </Button>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
      <span>
        Holding <span className="font-medium">{requestedDateLabel(hold.date)}</span> until{' '}
        {format(parseISO(hold.endsOn), 'MMM d')}
      </span>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={startEditing}>
        Change
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={() => {
          onError(null)
          release.mutate(undefined, { onError: (e) => onError(requestErrorMessage(e)) })
        }}
        pending={release.isPending}
      >
        Release
      </Button>
    </div>
  )
}
